import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useContacts } from '../../contacts/api/useContacts';
import { useCurrencies } from '../../currencies/api/useCurrencies';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { Loading } from '../../../shared/ui/Loading';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { EmptyState } from '../../../shared/ui/EmptyState';
import { FilterSheet } from '../../../shared/ui/FilterSheet';
import { useSales } from '../api/useTrades';

const LIMIT = 50;

const EMPTY = {
  status: '',
  paymentStatus: '',
  currencyId: '',
  contactId: '',
  dateFrom: '',
  dateTo: '',
};

export function SalesListPage() {
  const { t, i18n } = useTranslation();
  const [f, setF] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);

  const currencies = useCurrencies();
  const contacts = useContacts();

  function setField(key: string, value: string) {
    setPage(0);
    setF((prev) => ({ ...prev, [key]: value }));
  }

  const query = {
    ...(f.status ? { status: f.status } : {}),
    ...(f.paymentStatus ? { paymentStatus: f.paymentStatus } : {}),
    ...(f.currencyId ? { currencyId: f.currencyId } : {}),
    ...(f.contactId ? { contactId: f.contactId } : {}),
    ...(f.dateFrom ? { dateFrom: f.dateFrom } : {}),
    ...(f.dateTo ? { dateTo: f.dateTo } : {}),
    limit: LIMIT,
    offset: page * LIMIT,
  };

  const q = useSales(query);
  const activeFilterCount = Object.values(f).filter(Boolean).length;
  const totalPages = q.data ? Math.ceil(q.data.total / LIMIT) : 1;

  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  const dayFmt = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  type SaleItem = NonNullable<typeof q.data>['data'][number];
  const grouped: Map<string, SaleItem[]> = new Map();
  if (q.data) {
    for (const s of q.data.data) {
      const day = s.transactionDate.slice(0, 10);
      if (!grouped.has(day)) grouped.set(day, []);
      const bucket = grouped.get(day);
      if (bucket) bucket.push(s);
    }
  }

  const filteredGrouped: Map<string, SaleItem[]> = new Map();
  if (search.trim()) {
    const needle = search.trim().toLowerCase();
    for (const [day, rows] of grouped) {
      const filtered = rows.filter((s) => {
        const code = currencies.data?.find((c) => c.id === s.deliveredCurrencyId)?.code ?? '';
        return (
          s.deliveredAmount.includes(needle) ||
          s.paymentTotal.includes(needle) ||
          code.toLowerCase().includes(needle) ||
          (s.reference ?? '').toLowerCase().includes(needle)
        );
      });
      if (filtered.length > 0) filteredGrouped.set(day, filtered);
    }
  } else {
    for (const [day, rows] of grouped) filteredGrouped.set(day, rows);
  }

  return (
    <>
      <PageHeader
        title={t('sales.title')}
        action={
          <Link to="/sales/new" className="btn btn--primary">
            {t('sales.new')}
          </Link>
        }
      />

      <div className="search-row">
        <input
          className="search-row__input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
        />
        <button
          type="button"
          className={`filter-pill${activeFilterCount > 0 ? ' has-filters' : ''}`}
          aria-expanded={filterOpen}
          onClick={() => setFilterOpen(true)}
        >
          {t('common.filters')}
          {activeFilterCount > 0 ? (
            <span className="filter-pill__count">{activeFilterCount}</span>
          ) : null}
        </button>
      </div>

      {filterOpen ? (
        <FilterSheet onClose={() => setFilterOpen(false)}>
          <div className="field">
            <label>{t('sales.status_label')}</label>
            <select value={f.status} onChange={(e) => setField('status', e.target.value)}>
              <option value="">{t('common.filter.all_statuses')}</option>
              <option value="CONFIRMED">{t('sales.status.CONFIRMED')}</option>
              <option value="CANCELLED">{t('sales.status.CANCELLED')}</option>
              <option value="REVERSED">{t('sales.status.REVERSED')}</option>
            </select>
          </div>
          <div className="field">
            <label>{t('sales.payment_status_label')}</label>
            <select
              value={f.paymentStatus}
              onChange={(e) => setField('paymentStatus', e.target.value)}
            >
              <option value="">{t('common.filter.all_payment_statuses')}</option>
              <option value="UNPAID">{t('sales.payment_status.UNPAID')}</option>
              <option value="PARTIALLY_PAID">{t('sales.payment_status.PARTIALLY_PAID')}</option>
              <option value="PAID">{t('sales.payment_status.PAID')}</option>
            </select>
          </div>
          <div className="field">
            <label>{t('sales.from_currency')}</label>
            <select value={f.currencyId} onChange={(e) => setField('currencyId', e.target.value)}>
              <option value="">{t('common.filter.all_currencies')}</option>
              {currencies.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t('sales.contact_optional')}</label>
            <select value={f.contactId} onChange={(e) => setField('contactId', e.target.value)}>
              <option value="">{t('common.filter.all_contacts')}</option>
              {contacts.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t('common.filter.date_from')}</label>
            <input
              type="date"
              value={f.dateFrom}
              onChange={(e) => setField('dateFrom', e.target.value)}
            />
          </div>
          <div className="field">
            <label>{t('common.filter.date_to')}</label>
            <input
              type="date"
              value={f.dateTo}
              onChange={(e) => setField('dateTo', e.target.value)}
            />
          </div>
          {activeFilterCount > 0 ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setF(EMPTY);
                setPage(0);
              }}
            >
              {t('common.reset_filters')}
            </button>
          ) : null}
        </FilterSheet>
      ) : null}

      {q.isLoading ? <Loading /> : null}
      {q.error ? <ErrorMessage error={q.error} /> : null}

      {q.data && filteredGrouped.size === 0 ? (
        <EmptyState
          icon="🧾"
          title={t('sales.empty')}
          primaryAction={
            <Link to="/sales/new" className="btn btn--primary">
              {t('sales.new')}
            </Link>
          }
        />
      ) : null}

      {filteredGrouped.size > 0
        ? Array.from(filteredGrouped.entries()).map(([day, rows]) => {
            const dayDate = new Date(day + 'T00:00:00');
            return (
              <div key={day} className="day-group">
                <div className="day-group__header">
                  <span className="day-group__label">{dayFmt.format(dayDate)}</span>
                </div>
                <ul className="card-list">
                  {rows.map((s) => {
                    const code =
                      currencies.data?.find((c) => c.id === s.deliveredCurrencyId)?.code ?? '???';
                    return (
                      <li key={s.id}>
                        <Link to={`/sales/${s.id}`} className="card-row">
                          <div className="card-row__header">
                            <h2 className="card-row__title">
                              {s.deliveredAmount} {code}
                            </h2>
                            <span className={`badge badge--${paymentStatusClass(s.paymentStatus)}`}>
                              {t(`sales.payment_status.${s.paymentStatus}`)}
                            </span>
                          </div>
                          <div className="card-row__meta">
                            <span className="card-row__mono">
                              {dateFmt.format(new Date(s.transactionDate))}
                            </span>
                            <span>
                              {s.paymentTotal}{' '}
                              {currencies.data?.find((c) => c.id === s.paymentCurrencyId)?.code ??
                                ''}
                            </span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        : null}

      {q.data && q.data.total > LIMIT ? (
        <div className="pagination">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
          >
            {t('common.prev_page')}
          </button>
          <span className="pagination__info">
            {t('common.page_of', { n: page + 1, total: totalPages })}
          </span>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages - 1}
          >
            {t('common.next_page')}
          </button>
        </div>
      ) : null}
    </>
  );
}

function paymentStatusClass(status: string) {
  if (status === 'PAID') return 'in';
  if (status === 'PARTIALLY_PAID') return 'warn';
  return 'danger';
}
